import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import BibliaPlanes from './BibliaPlanes';
import BibliaAsignaciones from './BibliaAsignaciones';

const Biblia = () => {
  const API = `${window.location.origin}/api`;
  
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [chapter, setChapter] = useState(1);
  const [chapterData, setChapterData] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('read'); // read, search, favorites, plans, assignments
  const [userRole, setUserRole] = useState('estudiante');
  
  useEffect(() => {
    loadBooks();
    loadFavorites();
    loadUserRole();
  }, []);
  
  const loadUserRole = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // El endpoint devuelve el user directamente, no dentro de data.user
      const role = response.data.rol || response.data.user?.rol || 'estudiante';
      setUserRole(role);
      console.log('User role loaded:', role); // Para debugging
    } catch (error) {
      console.error('Error loading user role:', error);
      // Por defecto, asumir estudiante si falla
      setUserRole('estudiante');
    }
  };
  
  const loadBooks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/books`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooks(response.data.books);
    } catch (error) {
      console.error('Error loading books:', error);
      toast.error('Error al cargar libros de la Biblia');
    }
  };
  
  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data.favorites);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };
  
  const loadChapter = async (bookId, chapterNum) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/${bookId}/${chapterNum}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChapterData(response.data.data);
    } catch (error) {
      console.error('Error loading chapter:', error);
      toast.error('Error al cargar capítulo');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setChapter(1);
    loadChapter(book.id, 1);
  };
  
  const handleChapterChange = (newChapter) => {
    if (selectedBook && newChapter >= 1) {
      setChapter(newChapter);
      loadChapter(selectedBook.id, newChapter);
    }
  };
  
  const handleSearch = async () => {
    if (searchQuery.length < 3) {
      toast.warning('Escribe al menos 3 caracteres para buscar');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Detectar si es una referencia bíblica (formato: Libro Capitulo:Versiculo)
      // Ejemplos: "Hechos 2:38", "Juan 3:16", "Salmos 23:1", "1 Corintios 13:4"
      const referencePattern = /^(.+?)\s+(\d+):(\d+)$/i;
      const match = searchQuery.trim().match(referencePattern);
      
      if (match) {
        // Es una referencia bíblica
        const [, bookName, chapter, verse] = match;
        
        // Mapeo de nombres de libros en español a IDs
        const bookMapping = {
          'genesis': 'gen', 'génesis': 'gen',
          'exodo': 'exo', 'éxodo': 'exo',
          'levitico': 'lev', 'levítico': 'lev',
          'numeros': 'num', 'números': 'num',
          'deuteronomio': 'deut',
          'josue': 'jos', 'josué': 'jos',
          'jueces': 'jue',
          'rut': 'rut',
          '1 samuel': '1sa', '1samuel': '1sa',
          '2 samuel': '2sa', '2samuel': '2sa',
          '1 reyes': '1re', '1reyes': '1re',
          '2 reyes': '2re', '2reyes': '2re',
          '1 cronicas': '1cr', '1 crónicas': '1cr', '1cronicas': '1cr',
          '2 cronicas': '2cr', '2 crónicas': '2cr', '2cronicas': '2cr',
          'esdras': 'esd',
          'nehemias': 'neh', 'nehemías': 'neh',
          'ester': 'est',
          'job': 'job',
          'salmos': 'sal',
          'proverbios': 'pro',
          'eclesiastes': 'ecl', 'eclesiástes': 'ecl',
          'cantares': 'cant',
          'isaias': 'isa', 'isaías': 'isa',
          'jeremias': 'jer', 'jeremías': 'jer',
          'lamentaciones': 'lam',
          'ezequiel': 'eze',
          'daniel': 'dan',
          'oseas': 'ose',
          'joel': 'joel',
          'amos': 'amo', 'amós': 'amo',
          'abdias': 'abd', 'abdías': 'abd',
          'jonas': 'jon', 'jonás': 'jon',
          'miqueas': 'miq',
          'nahum': 'nah', 'nahúm': 'nah',
          'habacuc': 'hab',
          'sofonias': 'sof', 'sofonías': 'sof',
          'hageo': 'hag',
          'zacarias': 'zac', 'zacarías': 'zac',
          'malaquias': 'mal', 'malaquías': 'mal',
          'mateo': 'mat',
          'marcos': 'mar',
          'lucas': 'luc',
          'juan': 'juan',
          'hechos': 'hech',
          'romanos': 'rom',
          '1 corintios': '1cor', '1corintios': '1cor',
          '2 corintios': '2cor', '2corintios': '2cor',
          'galatas': 'gal', 'gálatas': 'gal',
          'efesios': 'efe',
          'filipenses': 'fil',
          'colosenses': 'col',
          '1 tesalonicenses': '1tes', '1tesalonicenses': '1tes',
          '2 tesalonicenses': '2tes', '2tesalonicenses': '2tes',
          '1 timoteo': '1tim', '1timoteo': '1tim',
          '2 timoteo': '2tim', '2timoteo': '2tim',
          'tito': 'tit',
          'filemon': 'file', 'filemón': 'file',
          'hebreos': 'heb',
          'santiago': 'sant',
          '1 pedro': '1ped', '1pedro': '1ped',
          '2 pedro': '2ped', '2pedro': '2ped',
          '1 juan': '1juan', '1juan': '1juan',
          '2 juan': '2juan', '2juan': '2juan',
          '3 juan': '3juan', '3juan': '3juan',
          'judas': 'judas',
          'apocalipsis': 'apoc'
        };
        
        const bookId = bookMapping[bookName.toLowerCase().trim()];
        
        if (bookId) {
          // Buscar el versículo específico
          const response = await axios.get(`${API}/bible/${bookId}/${chapter}/${verse}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success && response.data.data) {
            setSearchResults([response.data.data]);
            toast.success('Versículo encontrado');
          } else {
            toast.error('Versículo no encontrado');
            setSearchResults([]);
          }
        } else {
          toast.error(`Libro "${bookName}" no encontrado. Intenta con el nombre completo en español.`);
          setSearchResults([]);
        }
      } else {
        // Búsqueda por palabras (búsqueda normal)
        const response = await axios.get(`${API}/bible/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSearchResults(response.data.results);
        if (response.data.results.length === 0) {
          toast.info('No se encontraron resultados');
        }
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Error al buscar');
    } finally {
      setLoading(false);
    }
  };
  
  const addToFavorites = async (verse) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/bible/favorites`, verse, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Versículo agregado a favoritos');
      loadFavorites();
    } catch (error) {
      console.error('Error adding favorite:', error);
      toast.error(error.response?.data?.detail || 'Error al agregar favorito');
    }
  };
  
  const removeFavorite = async (favoriteId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/bible/favorites/${favoriteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Favorito eliminado');
      loadFavorites();
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Error al eliminar favorito');
    }
  };
  
  const copyToClipboard = (text, reference) => {
    navigator.clipboard.writeText(`${text}\n\n${reference} (RVR1960)`);
    toast.success('Versículo copiado al portapapeles');
  };

  const copyVerse = (verseText, verseNum) => {
    const reference = `${selectedBook.name} ${chapter}:${verseNum}`;
    copyToClipboard(verseText, reference);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6">
      {/* Header hermoso con gradiente */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center">
                <span className="text-5xl mr-3">📖</span>
                Santa Biblia
              </h1>
              <p className="text-indigo-100 text-lg">Reina Valera 1960</p>
            </div>
            <div className="hidden md:block">
              <div className="text-right">
                <p className="text-sm text-indigo-200">Tu herramienta de estudio</p>
                <p className="text-2xl font-semibold">66 Libros • 1,189 Capítulos</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs modernos - Responsive */}
      <div className="max-w-7xl mx-auto mb-6 px-2 sm:px-4">
        <div className="bg-white rounded-2xl shadow-lg p-2 flex flex-wrap sm:flex-nowrap gap-2">
          <button
            onClick={() => setActiveTab('read')}
            className={`flex-1 min-w-[45%] sm:min-w-0 py-2 sm:py-3 px-3 sm:px-6 rounded-xl text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'read'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            📚 Leer
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 min-w-[45%] sm:min-w-0 py-2 sm:py-3 px-3 sm:px-6 rounded-xl text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'search'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🔍 Buscar
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex-1 min-w-[45%] sm:min-w-0 py-2 sm:py-3 px-3 sm:px-6 rounded-xl text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'favorites'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ⭐ Favoritos ({favorites.length})
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`flex-1 min-w-[45%] sm:min-w-0 py-2 sm:py-3 px-3 sm:px-6 rounded-xl text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'plans'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            📋 Planes
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`flex-1 min-w-[45%] sm:min-w-0 py-2 sm:py-3 px-3 sm:px-6 rounded-xl text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'assignments'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            📚 Asignaciones
          </button>
        </div>
      </div>
      
      {/* Contenido */}
      <div className="max-w-7xl mx-auto">
        {/* Tab: Leer - RESPONSIVE LAYOUT */}
        {activeTab === 'read' && (
          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            
            {/* Selector de libros - DROPDOWN EN MÓVIL */}
            {!selectedBook && (
              <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-gray-800">Selecciona un Libro</h3>
                
                {/* Dropdown para móvil */}
                <div className="block md:hidden mb-4">
                  <select
                    onChange={(e) => {
                      const book = books.find(b => b.id === e.target.value);
                      if (book) handleBookSelect(book);
                    }}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-base focus:border-indigo-600 focus:outline-none"
                  >
                    <option value="">-- Selecciona un libro --</option>
                    {books.map((book) => (
                      <option key={book.id} value={book.id}>
                        {book.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Grid para tablet/desktop */}
                <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {books.map((book) => (
                    <button
                      key={book.id}
                      onClick={() => handleBookSelect(book)}
                      className="w-full text-left px-4 py-3 rounded-xl transition-all bg-gray-50 hover:bg-indigo-100 text-gray-700 font-medium"
                    >
                      {book.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Visor de capítulos - OCUPA TODO EL ANCHO */}
            {selectedBook && (
              <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
                
                {/* Botón volver (solo móvil) */}
                <button
                  onClick={() => setSelectedBook(null)}
                  className="md:hidden mb-4 text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-2"
                >
                  ← Volver a libros
                </button>
                
                {/* Selector de libro actual (móvil) */}
                <div className="block md:hidden mb-4">
                  <select
                    value={selectedBook.id}
                    onChange={(e) => {
                      const book = books.find(b => b.id === e.target.value);
                      if (book) handleBookSelect(book);
                    }}
                    className="w-full px-4 py-3 border-2 border-indigo-300 rounded-xl text-base font-bold focus:border-indigo-600 focus:outline-none"
                  >
                    {books.map((book) => (
                      <option key={book.id} value={book.id}>
                        {book.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Título del libro (desktop) */}
                <div className="hidden md:block mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">{selectedBook.name}</h2>
                </div>
                
                {/* Navegación de capítulos */}
                <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 p-3 sm:p-4 rounded-xl gap-2">
                  <button
                    onClick={() => handleChapterChange(chapter - 1)}
                    disabled={chapter === 1}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-white rounded-lg shadow hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm sm:text-base font-semibold"
                  >
                    ← Anterior
                  </button>
                  <div className="text-center">
                    <div className="text-xs sm:text-sm text-indigo-600 font-semibold">Capítulo</div>
                    <div className="text-xl sm:text-2xl font-bold text-indigo-900">{chapter}</div>
                  </div>
                  <button
                    onClick={() => handleChapterChange(chapter + 1)}
                    disabled={!chapterData || chapter >= (chapterData.total_chapters || 999)}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-white rounded-lg shadow hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm sm:text-base font-semibold"
                  >
                    Siguiente →
                  </button>
                </div>

                {/* Contenido del capítulo - ANCHO COMPLETO */}
                {loading ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">⏳</div>
                    <div className="text-gray-600">Cargando capítulo...</div>
                  </div>
                ) : chapterData ? (
                  <div className="space-y-4">
                    {chapterData.verses.map((verseObj) => {
                      // La API devuelve objetos con {verse: number, text: string}
                      const verseNum = verseObj.verse || 1;
                      const verseText = verseObj.text || verseObj;
                      return (
                        <div key={verseNum} className="flex gap-3 group">
                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xs sm:text-sm shadow-md">
                            {verseNum}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-base sm:text-lg text-gray-800 leading-relaxed break-words whitespace-pre-line">
                              {verseText.replace(/\/n/g, '\n')}
                            </p>
                            <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => copyVerse(verseText, verseNum)}
                                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                              >
                                📋 Copiar
                              </button>
                              <button
                                onClick={() => addToFavorites({
                                  book_id: selectedBook.id,
                                  chapter: chapter,
                                  verse: verseNum
                                })}
                                className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded-lg transition-all"
                              >
                                ⭐ Favorito
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No se pudo cargar el capítulo
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'search' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
              <span className="text-4xl mr-3">🔍</span>
              Buscar en la Biblia
            </h2>
            
            {/* Barra de búsqueda */}
            <div className="mb-4">
              <div className="flex gap-3 mb-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Busca por referencia o palabras... (ej: Hechos 2:38, Juan 3:16, amor, fe)"
                  className="flex-1 px-6 py-4 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none text-lg"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
              <div className="text-base text-gray-600 px-2 py-2 bg-blue-50 rounded-lg border border-blue-200">
                💡 <strong>Ejemplos de búsqueda:</strong> "Hechos 2:38", "Juan 3:16", "Salmos 23:1", "1 Corintios 13:4" o palabras como "amor", "fe", "paz"
              </div>
            </div>
            
            {/* Resultados */}
            {searchResults.length > 0 && (
              <div>
                <p className="text-gray-600 mb-4">
                  Se encontraron <span className="font-bold text-indigo-600">{searchResults.length}</span> resultados
                </p>
                <div className="space-y-4">
                  {searchResults.map((result, index) => (
                    <div
                      key={index}
                      className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-l-4 border-indigo-600 hover:shadow-lg transition-all"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-indigo-600 text-lg">{result.reference}</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => copyToClipboard(result.text, result.reference)}
                            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => addToFavorites({
                              book_id: result.book_id,
                              chapter: result.chapter,
                              verse: result.verse
                            })}
                            className="p-2 bg-gray-200 hover:bg-yellow-400 hover:text-white rounded-lg"
                          >
                            ⭐
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{result.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'favorites' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
              <span className="text-4xl mr-3">⭐</span>
              Mis Versículos Favoritos
            </h2>
            
            {favorites.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-8xl mb-4">⭐</div>
                <h3 className="text-2xl font-bold text-gray-700 mb-2">No tienes favoritos aún</h3>
                <p className="text-gray-500">Marca tus versículos favoritos mientras lees la Biblia</p>
              </div>
            ) : (
              <div className="space-y-4">
                {favorites.map((fav) => (
                  <div
                    key={fav.id}
                    className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-l-4 border-yellow-400 hover:shadow-lg transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-yellow-700 text-lg">{fav.reference}</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(fav.text, fav.reference)}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => removeFavorite(fav.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-2">{fav.text}</p>
                    {fav.note && (
                      <p className="text-sm text-gray-600 italic bg-white p-3 rounded-lg">
                        📝 {fav.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'plans' && (
          <BibliaPlanes />
        )}
        
        {activeTab === 'assignments' && (
          <BibliaAsignaciones userRole={userRole} />
        )}
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #6366f1, #9333ea);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #4f46e5, #7c3aed);
        }
      `}} />
    </div>
  );
};

export default Biblia;
